import { useEffect } from 'react'
import {
  DEFAULT_INACTIVITY_MINUTES,
  getInactivityTimeoutMs,
  isInactive,
  recordActivity,
  signOutForInactivity,
} from './inactivity'

// How often an open page checks the clock, and how often a burst of
// interaction is written to storage.
const CHECK_INTERVAL_MS = 30_000
const RECORD_THROTTLE_MS = 5_000

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const

/**
 * Ends the session once there has been no interaction for the configured
 * time. Checked on mount, so a browser reopened after a long absence signs
 * out before anything is shown, and again on a timer and whenever the page
 * becomes visible, so an open tab does not outlive its timeout either.
 */
export function useInactivitySignOut(active: boolean): void {
  useEffect(() => {
    if (!active) return

    let timeoutMs = DEFAULT_INACTIVITY_MINUTES * 60_000
    let signedOut = false
    let lastWrite = 0

    function expireIfInactive() {
      if (signedOut || !isInactive(timeoutMs)) return
      signedOut = true
      void signOutForInactivity()
    }

    function onActivity() {
      const now = Date.now()
      if (now - lastWrite < RECORD_THROTTLE_MS) return
      // A page left open past its timeout does not revive by being touched.
      if (isInactive(timeoutMs, now)) {
        expireIfInactive()
        return
      }
      lastWrite = now
      recordActivity(now)
    }

    function onVisibility() {
      if (document.visibilityState === 'visible') expireIfInactive()
    }

    // Judged against the configured timeout, so the first check waits for it:
    // recording this visit as activity before then would hide how long the
    // session had been idle.
    let cancelled = false
    void getInactivityTimeoutMs().then((configured) => {
      if (cancelled) return
      timeoutMs = configured
      expireIfInactive()
      if (!signedOut) {
        recordActivity()
        lastWrite = Date.now()
      }
    })

    for (const name of ACTIVITY_EVENTS) window.addEventListener(name, onActivity, { passive: true })
    document.addEventListener('visibilitychange', onVisibility)
    const timer = window.setInterval(expireIfInactive, CHECK_INTERVAL_MS)

    return () => {
      cancelled = true
      for (const name of ACTIVITY_EVENTS) window.removeEventListener(name, onActivity)
      document.removeEventListener('visibilitychange', onVisibility)
      window.clearInterval(timer)
    }
  }, [active])
}
